import * as Sentry from "@sentry/nextjs";
import { msg } from "common/stores/SnackbarStore";
import { GenericObject } from "common/types/Survey";
import { loginRedirect } from "common/utils/auth/AuthUtils";
import { publicPaths } from "common/utils/auth/RouteGuard";
import ky, { KyResponse, Options } from "ky";
import type { SWRResponse } from "swr";

/**
 * Base class for Monarch server implementations. In implementations, please use the functions
 * defined here to make requests to the backend. Each function checks whether a monarch URL
 * exists, if it does not, it retrieves it, then proceeds with the request. If you would like
 * to use request methods aside from get/post/put, please add them here.
 */

// Handles client side 401 Unauthorized responses by redirecting to the login page.
// If this error occurs server-side, it will be handled by the caller instead
// since redirection might not be desirable in that context.
function handleUnauthorized() {
	const path = window.location.pathname;

	// Note: If the route already has a returnUrl query parameter, ensure we don’t overwrite or duplicate it.
	let query = { returnUrl: null, username: null };
	const urlParams = new URLSearchParams(window.location.search);

	// If the path isn't public, set the returnUrl query param to redirect back after login.
	if (!publicPaths.includes(path)) {
		query.returnUrl = path;
	}

	// Allow parameters from the backend to pass to the login page if `error` is present.
	const errorParam = urlParams.get("error");

	// Special handling for survey paths
	if (path.includes("survey")) {
		query.returnUrl = "/";
	}

	// Redirect to the login page with relevant query parameters
	loginRedirect(
		"auth0/login",
		query.returnUrl,
		errorParam ? `?error=${new URLSearchParams(errorParam)}` : "",
		query.username
	);
}

function handleError(error): KyResponse | never {
	if (error?.name === "AbortError") {
		throw error;
	}
	if (
		error?.name === "HTTPError" &&
		error.response?.status === 401 &&
		typeof window !== "undefined"
	) {
		handleUnauthorized();
	}
	if (error?.response?.status === 409) {
		// If the error is a 409, we can log it as a warning since it might be expected
		console.warn("Multiple Tabs", { error });
		msg.set({
			message:
				"Looks like your data changed elsewhere. Refresh this page to get the latest updates, and stick to one tab to avoid any hiccups.",
			autoHide: false,
			severity: "error",
		});
		return null; // Return null to indicate no data was fetched
	} else {
		Sentry.captureException(error);
		console.error("API Query Error: ", { error });
		throw error;
	}
}

export const swrErrorHandle = ({
	key = "no_key",
	result,
	params = undefined,
	expectArray = false,
	userFacingError = false,
}: {
	key: string | null;
	result: SWRResponse;
	params?: GenericObject;
	expectArray?: boolean;
	userFacingError?: boolean;
}) => {
	const { data, error, isLoading } = result;
	let errorMessage = "";
	if (error) {
		errorMessage = `${error.message}: ${JSON.stringify({ key, params, result })}`;
	} else if (!isLoading && data && expectArray && !Array.isArray(data)) {
		// If we expect an array but get an object, this is likely an error response with a mis-matched response code, throw an error
		errorMessage = `${data.detail?.error}: ${JSON.stringify({ key, params, result })}`;
	}

	if (errorMessage) {
		if (
			[401, 403].includes(error?.response?.status) ||
			error?.message === "Failed to fetch" ||
			errorMessage.includes("Failed to fetch") ||
			data?.detail?.error === "Authentication Error" ||
			errorMessage.includes("The operation was aborted")
		) {
			console.warn(errorMessage);
			// Could send these to sentry with a very low severity.
			// these are expected response codes when a user's session has expired, etc.
			// Sentry.captureMessage(errorMessage, "info");
		} else if ([404].includes(error?.response?.status)) {
			// many GET requests will return 404 if the resource was not found, such as requesting a tag by name that does not exist.
			// report to sentry as a less than an an error. - could be a visiting a bookmarked link before getting cookie etc.
			console.warn(errorMessage);
			Sentry.captureMessage(errorMessage, "info");
		} else {
			console.error("SWR Error Catch", errorMessage);
			const err = new Error(errorMessage);
			Sentry.captureException(err);
			if (userFacingError) {
				msg.set({
					message: data?.detail?.error || errorMessage,
					autoHide: false,
					severity: "error",
				});
			}
		}
	}
};

const isRetryEnv = process.env.DEPLOY_ENV !== "prod";

export const retryConfig = isRetryEnv
	? {
			limit: 1,
			statusCodes: [502],
			methods: ["get", "post", "put", "delete", "patch"],
		}
	: 0;

// In the browser, anchor API calls to the current page's origin instead of the
// build-time-baked BASE_FE_URL. Otherwise users on the canonical alias send
// API traffic to the temp alias during the SST v2→v3 cutover (and vice
// versa). On the server (SSR / middleware), there is no window, so fall back
// to the env var.
const apiOrigin =
	typeof window !== "undefined"
		? window.location.origin
		: process.env.BASE_FE_URL;

export default class MonarchClient {
	static api = ky.extend({
		prefixUrl: apiOrigin + "/facade",
		credentials: "include",
		timeout: 60 * 1000,
		retry: retryConfig,
		hooks: {
			beforeError: [
				(error) => {
					const { response } = error;
					if (response?.status === 401 && typeof window !== "undefined") {
						// Handle the 401 error and redirect
						handleUnauthorized();
					}
					return error;
				},
			],
		},
	});

	static async get(
		endpoint: string | URL,
		options?: Options
	): Promise<KyResponse> {
		return MonarchClient.api
			.get(endpoint, options)
			.catch((e) => handleError(e));
	}

	static async post(
		endpoint: string | URL,
		options?: Options
	): Promise<KyResponse> {
		return MonarchClient.api
			.post(endpoint, options)
			.catch((e) => handleError(e));
	}

	static async put(
		endpoint: string | URL,
		options?: Options
	): Promise<KyResponse> {
		return MonarchClient.api
			.put(endpoint, options)
			.catch((e) => handleError(e));
	}

	static async delete(
		endpoint: string | URL,
		options?: Options
	): Promise<KyResponse> {
		return MonarchClient.api
			.delete(endpoint, options)
			.catch((e) => handleError(e));
	}

	static async patch(
		endpoint: string | URL,
		options?: Options
	): Promise<KyResponse> {
		return MonarchClient.api
			.patch(endpoint, options)
			.catch((e) => handleError(e));
	}
}

export const prepareSearchParams = (searchParams) => {
	const urlSearchParams = new URLSearchParams();
	const filteredParams = prepareJson(searchParams);

	for (const [key, value] of Object.entries(filteredParams)) {
		if (Array.isArray(value)) {
			// Default behavior of URLSearchParams constructor when passed an array results in something like:
			// ?myArray=foo%2Cbar%2Cfizz%2Cbuzz (simply the result of .toString() on the array, with escaped comma characters)
			// Optera API expects duplicate keys, eg:
			// ?myArray=foo&myArray=bar&myArray=fizz&myArray=buzz
			// Order matters. Preserve it. (sorting params, etc)
			value.forEach((arrayValue) => {
				urlSearchParams.append(key, arrayValue);
			});
		} else {
			urlSearchParams.append(key, String(value));
		}
	}

	return urlSearchParams;
};

export const prepareJson = (searchParams) => {
	if (!searchParams) {
		return {};
	}
	// filter out nullish values from the search params
	const filteredSearchParams = Object.fromEntries(
		Object.entries(searchParams).filter(
			([, value]) =>
				value !== null &&
				value !== undefined &&
				value !== "" &&
				!(Array.isArray(value) && value.length === 0)
		)
	);

	Object.entries(filteredSearchParams).forEach(([key, value]) => {
		if (Array.isArray(value)) {
			const filteredArray: Array<any> = [];
			value.forEach((val) => {
				if (val !== null && val !== undefined && val !== "") {
					filteredArray.push(val);
				}
			});
			if (filteredArray.length > 0) {
				filteredSearchParams[key] = filteredArray;
			} else {
				delete filteredSearchParams[key];
			}
		} else if (typeof value === "object") {
			// treat nested objects recursively
			filteredSearchParams[key] = prepareJson(value);
		} else if (typeof value === "number") {
			filteredSearchParams[key] = Number(value);
		} else {
			filteredSearchParams[key] = String(value);
		}
	});

	return filteredSearchParams;
};
